import { Outlet } from 'react-router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast, Toaster } from 'sonner';
import { Navbar } from './Navbar';
import { BottomNav } from './BottomNav';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthInit } from '@/hooks/useAuthInit';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { COLORS } from '@nzassa/shared';

export function RootLayout() {
  const { t } = useTranslation();
  useAuthInit();
  useScrollToTop();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const settingsLoaded = useSettingsStore((s) => s.loaded);
  const fetchPublicSettings = useSettingsStore((s) => s.fetchPublicSettings);

  const { isOnline } = useOnlineStatus();
  const { canInstall, install } = usePwaInstall();
  const [installDismissed, setInstallDismissed] = useState(false);

  useEffect(() => {
    if (!settingsLoaded) {
      fetchPublicSettings();
    }
  }, [settingsLoaded, fetchPublicSettings]);

  useEffect(() => {
    if (!isOnline) {
      toast.warning(t('common.hors_ligne'), { duration: Infinity, id: 'offline' });
    } else {
      toast.dismiss('offline');
    }
  }, [isOnline, t]);

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster position="top-center" richColors closeButton duration={4000} visibleToasts={3} toastOptions={{ className: 'text-sm' }} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-lg"
        style={{ color: COLORS.blue }}
      >
        {t('nav.aller_contenu')}
      </a>
      <Navbar />

      <main id="main-content" tabIndex={-1} className="flex-1 pb-16 sm:pb-0 outline-none">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* PWA Install Banner */}
      {canInstall && !installDismissed && (
        <div className="fixed bottom-20 sm:bottom-4 left-4 right-4 z-40 sm:left-auto sm:right-4 sm:max-w-sm">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: COLORS.blue }}>
              <img src="/logos/nzassalearn-compact.svg" alt="" className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">{t('pwa.installer')}</p>
              <p className="text-xs text-slate-500 truncate">{t('pwa.acces_rapide')}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => setInstallDismissed(true)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1">{t('common.fermer')}</button>
              <button onClick={install} className="text-xs font-bold text-white px-3 py-1.5 rounded-xl" style={{ background: COLORS.orange }}>{t('pwa.installer_btn')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation — mobile only */}
      {isAuthenticated && <BottomNav />}

      {/* Coach IA removed — feature disabled for CICN pilot */}

      {/* Offline indicator banner */}
      <OfflineIndicator />
    </div>
  );
}
